import type { AgeType } from '@/types/billing';

export type StrictRate = {
  age_type: AgeType;
  group_type: string;
  service_code: string;
  day_of_week: number; // 1=Mon ... 7=Sun
  daily_rate: number;
  subsidised: boolean;
  is_extra_day: boolean;
  service_name?: string;
};

export function annotateRatesWithGroupType<T extends Record<string, unknown>>(rates: T[] | unknown, groupType: string): Array<T & { group_type: string }> {
  const arr = Array.isArray(rates) ? (rates as T[]) : [];
  return arr.map((r) => ({ ...r, group_type: groupType })) as Array<T & { group_type: string }>;
}

export function assertStrictRate(rate: unknown): asserts rate is StrictRate {
  const missing: string[] = [];
  if (!rate || typeof rate !== 'object') throw new Error('Invalid rate payload: not an object');
  // Narrowing `unknown` with `typeof … === 'object'` yields `object`, which has
  // no index signature — the reads below need a keyed view of the same value.
  const candidate: Record<string, unknown> = { ...rate };
  if (typeof candidate.age_type !== 'string') missing.push('age_type');
  if (typeof candidate.group_type !== 'string') missing.push('group_type');
  if (typeof candidate.service_code !== 'string') missing.push('service_code');
  if (typeof candidate.day_of_week !== 'number') missing.push('day_of_week');
  if (typeof candidate.daily_rate !== 'number') missing.push('daily_rate');
  if (typeof candidate.subsidised !== 'boolean') missing.push('subsidised');
  if (typeof candidate.is_extra_day !== 'boolean') missing.push('is_extra_day');
  if (missing.length > 0) {
    throw new Error(`Rate payload missing required fields: ${missing.join(', ')}`);
  }
}

export function normalizeAndValidateRates(rawRates: unknown, groupType: string): StrictRate[] {
  const annotated = annotateRatesWithGroupType(rawRates as Record<string, unknown>[], groupType);
  return annotated.map((rate) => {
    // Ensure types: boolean for subsidised/is_extra_day; number for numeric fields
    const normalized: Record<string, unknown> = { ...rate };
    if (typeof normalized.subsidised !== 'boolean') {
      if (normalized.subsidised === 1 || normalized.subsidised === '1' || normalized.subsidised === 'true') normalized.subsidised = true;
      else if (normalized.subsidised === 0 || normalized.subsidised === '0' || normalized.subsidised === 'false') normalized.subsidised = false;
    }
    if (typeof normalized.is_extra_day !== 'boolean') {
      if (normalized.is_extra_day === 1 || normalized.is_extra_day === '1' || normalized.is_extra_day === 'true') normalized.is_extra_day = true;
      else if (normalized.is_extra_day === 0 || normalized.is_extra_day === '0' || normalized.is_extra_day === 'false') normalized.is_extra_day = false;
    }
    if (typeof normalized.day_of_week === 'string') normalized.day_of_week = Number.parseInt(normalized.day_of_week, 10);
    if (typeof normalized.daily_rate === 'string') normalized.daily_rate = Number.parseInt(normalized.daily_rate, 10);

    assertStrictRate(normalized);
    return normalized as StrictRate;
  });
}

export function findMatchingRateStrict(
  rates: StrictRate[],
  ageType: AgeType,
  groupType: string,
  // Optional: service-pattern entries parsed out of a stored contract can be
  // missing their code. An undefined code simply matches no rate, which every
  // caller already handles via the `undefined` return.
  serviceCode: string | undefined,
  dayOfWeek: number,
  subsidised: boolean,
  preferBookedOverExtra: boolean = true
): StrictRate | undefined {
  const candidates = rates.filter((r) =>
    r.age_type === ageType &&
    r.group_type === groupType &&
    r.service_code === serviceCode &&
    r.day_of_week === dayOfWeek &&
    r.subsidised === subsidised
  );

  if (candidates.length <= 1) return candidates[0];

  if (preferBookedOverExtra) {
    return candidates.find((r) => !r.is_extra_day) || candidates[0];
  }
  return candidates[0];
}


